import type { Figure } from "@howcomet/shared";
import type { FigureProps } from "./registry";

const COLORS = [
  "var(--color-cyan)",
  "var(--color-mint)",
  "var(--color-lilac)",
  "var(--color-pink)",
  "var(--color-gold)",
];

/**
 * Steps left to right, with arrows between them and none after the last.
 *
 * That absence is load-bearing. The missing arrow at the end is the only thing
 * separating this from a cycle, and the difference between the two is a factual
 * claim about the world — so the chain has to visibly stop rather than trail
 * off. A child who sees a loop believes it loops.
 *
 * Wraps rather than shrinking: five boxes across a phone would be forty pixels
 * each, and these have to be read aloud.
 */
export function SequenceFigure({ figure }: FigureProps<Figure>) {
  const labels = figure.labels ?? [];
  if (labels.length < 2) return null;

  return (
    <div
      className="flex flex-wrap items-stretch justify-center gap-x-1 gap-y-2"
      role="img"
      aria-label={`In order: ${labels.join(", then ")}. Then it is finished.`}
    >
      {labels.map((label, i) => (
        <div key={label} className="flex items-center gap-1">
          <div
            className="band-in disp flex min-h-[54px] max-w-[150px] items-center justify-center rounded-xl px-3 py-2 text-center text-[14px] leading-tight text-night"
            style={{ background: COLORS[i % COLORS.length], animationDelay: `${i * 0.1}s` }}
          >
            {label}
          </div>
          {i < labels.length - 1 && (
            /*
              The arrows light up one after another, so something is visibly
              travelling through the steps rather than the steps just sitting
              in a row. The bands themselves stay put: a child reads those, and
              text that pulses is text that is harder to read.

              Staggered across the whole run, so the pulse arrives at each
              arrow in turn instead of every arrow blinking together.
            */
            <span
              aria-hidden
              className="step-pulse shrink-0 text-[20px] text-cream"
              style={{ animationDelay: `${(i * 3.2) / Math.max(labels.length - 1, 1)}s` }}
            >
              →
            </span>
          )}
        </div>
      ))}
    </div>
  );
}
